import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import UploadStatementDialog from '../components/UploadStatementDialog';
import { fetchPaymentsReconciliation } from '../redux/slices/paymentsReconciliationSlice';

const MAX_RECONCILIATION_PERIOD_MONTHS = 2;

const getErrorMessage = (error) => {
  if (!error) {
    return '';
  }

  if (typeof error === 'string') {
    return error;
  }

  if (Array.isArray(error.errors)) {
    return error.errors.map(({ field, msg, message }) => `${field}: ${message || msg}`).join(', ');
  }

  return error.message || 'An error occured please try again!';
};

const formatAmount = (value) => `${Number(value || 0).toFixed(2)} Lei`;

const formatDate = (value) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-');

const formatList = (values, formatter = (value) => value) => (values?.length ? values.map(formatter).join(', ') : '-');

const errorCellSx = {
  color: 'error.main',
  fontWeight: 700,
};

function PaymentsReconciliationTable({ rows }) {
  const { t } = useTranslation();

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('Order ID')}</TableCell>
            <TableCell>{t('Transaction Type')}</TableCell>
            <TableCell>{t('Application Transaction')}</TableCell>
            <TableCell>{t('Application Date')}</TableCell>
            <TableCell align="right">{t('Expected Amount')}</TableCell>
            <TableCell align="right">{t('Bank Amount')}</TableCell>
            <TableCell align="right">{t('Difference')}</TableCell>
            <TableCell align="right">{t('Statement Entries')}</TableCell>
            <TableCell>{t('Bank Transaction IDs')}</TableCell>
            <TableCell>{t('Bank Posted Dates')}</TableCell>
            <TableCell>{t('Statement References')}</TableCell>
            <TableCell>{t('Payment RRN')}</TableCell>
            <TableCell>{t('Payment Processor')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => {
            const disparities = new Set(row.disparity_types || []);
            const disparityTypes = [...disparities];
            const hasMissingBankTransaction = disparityTypes.some((type) => type.startsWith('MISSING_BANK_'));
            const hasUnexpectedBankTransaction = disparityTypes.some((type) => type.startsWith('UNEXPECTED_BANK_'));
            const hasAmountMismatch = disparityTypes.some((type) => type.endsWith('_AMOUNT_MISMATCH'));
            const applicationCellSx = hasUnexpectedBankTransaction ? errorCellSx : undefined;
            const bankCellSx = hasMissingBankTransaction || hasUnexpectedBankTransaction ? errorCellSx : undefined;
            const amountCellSx =
              hasAmountMismatch || hasMissingBankTransaction || hasUnexpectedBankTransaction ? errorCellSx : undefined;
            const rowKey = [
              row.order_id,
              row.transaction_type,
              row.application_transaction_id || 'bank-only',
              row.statement_transaction_ids?.join('-') || 'no-bank',
              index,
            ].join(':');

            return (
              <TableRow key={rowKey}>
                <TableCell>{row.order_id}</TableCell>
                <TableCell>{t(row.transaction_type)}</TableCell>
                <TableCell sx={applicationCellSx}>{row.application_transaction_id || '-'}</TableCell>
                <TableCell sx={applicationCellSx}>{formatDate(row.application_transaction_at)}</TableCell>
                <TableCell align="right" sx={amountCellSx}>
                  {formatAmount(row.expected_amount)}
                </TableCell>
                <TableCell align="right" sx={amountCellSx}>
                  {formatAmount(row.bank_amount)}
                </TableCell>
                <TableCell align="right" sx={amountCellSx}>
                  {formatAmount(row.difference)}
                </TableCell>
                <TableCell align="right" sx={bankCellSx}>
                  {row.statement_entries_count}
                </TableCell>
                <TableCell sx={bankCellSx}>{formatList(row.statement_transaction_ids)}</TableCell>
                <TableCell sx={bankCellSx}>
                  {formatList(row.bank_posted_dates, (date) => dayjs(date).format('DD/MM/YYYY'))}
                </TableCell>
                <TableCell sx={bankCellSx}>{formatList(row.statement_references)}</TableCell>
                <TableCell sx={bankCellSx}>{formatList(row.payment_rrns)}</TableCell>
                <TableCell sx={bankCellSx}>{formatList(row.payment_processors)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function Payments() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [ordersStartDate, setOrdersStartDate] = useState(dayjs().startOf('month'));
  const [ordersEndDate, setOrdersEndDate] = useState(dayjs().endOf('day'));
  const [transactionsStartDate, setTransactionsStartDate] = useState(dayjs().startOf('month'));
  const [transactionsEndDate, setTransactionsEndDate] = useState(dayjs().endOf('day'));
  const [openUpload, setOpenUpload] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const { data: rows, error, isLoading, lastQuery } = useSelector((state) => state.paymentsReconciliation);

  useEffect(() => {
    if (error) {
      toast.error(t(getErrorMessage(error)), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  }, [error, t]);

  const updateStartDate = (value, endDate, setStartDate, setEndDate) => {
    setStartDate(value);

    if (!value) {
      return;
    }

    const latestEndDate = value.add(MAX_RECONCILIATION_PERIOD_MONTHS, 'month');
    if (!endDate || endDate.isBefore(value) || endDate.isAfter(latestEndDate)) {
      setEndDate(latestEndDate.isAfter(dayjs()) ? dayjs().endOf('day') : latestEndDate.endOf('day'));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setSubmitted(true);

    if (!ordersStartDate || !ordersEndDate || !transactionsStartDate || !transactionsEndDate) {
      toast.error(t('All date fields are required'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return;
    }

    if (ordersEndDate.isBefore(ordersStartDate) || transactionsEndDate.isBefore(transactionsStartDate)) {
      toast.error(t('Each end date must be after its start date'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return;
    }

    if (
      ordersEndDate.isAfter(ordersStartDate.add(MAX_RECONCILIATION_PERIOD_MONTHS, 'month')) ||
      transactionsEndDate.isAfter(transactionsStartDate.add(MAX_RECONCILIATION_PERIOD_MONTHS, 'month'))
    ) {
      toast.error(t('Each selected period cannot exceed two months'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return;
    }

    dispatch(
      fetchPaymentsReconciliation({
        ordersStartDate: dayjs(ordersStartDate).startOf('day').toISOString(),
        ordersEndDate: dayjs(ordersEndDate).endOf('day').toISOString(),
        transactionsStartDate: dayjs(transactionsStartDate).startOf('day').toISOString(),
        transactionsEndDate: dayjs(transactionsEndDate).endOf('day').toISOString(),
      })
    );
  };

  const toggleUpload = () => setOpenUpload((open) => !open);

  return (
    <div className="payments">
      <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
        <Grid item>
          <Typography variant="h3">{t('Payments')}</Typography>
        </Grid>
        <Grid item>
          <Button variant="contained" onClick={toggleUpload}>
            {t('Upload Statements')}
          </Button>
        </Grid>
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Paper component="form" onSubmit={handleSubmit} sx={{ p: 3, mb: 3 }}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} md>
              <DatePicker
                disableFuture
                value={ordersStartDate}
                onChange={(value) => updateStartDate(value, ordersEndDate, setOrdersStartDate, setOrdersEndDate)}
                sx={{ marginRight: 3 }}
                slotProps={{
                  textField: {
                    label: t('Orders Start Date'),
                    fullWidth: true,
                    required: true,
                    variant: 'outlined',
                    error: submitted && !ordersStartDate,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md>
              <DatePicker
                disableFuture
                value={ordersEndDate}
                minDate={ordersStartDate}
                maxDate={ordersStartDate ? ordersStartDate.add(MAX_RECONCILIATION_PERIOD_MONTHS, 'month') : undefined}
                onChange={(value) => setOrdersEndDate(value)}
                sx={{ marginRight: 3 }}
                slotProps={{
                  textField: {
                    label: t('Orders End Date'),
                    fullWidth: true,
                    required: true,
                    variant: 'outlined',
                    error: submitted && !ordersEndDate,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md>
              <DatePicker
                disableFuture
                value={transactionsStartDate}
                onChange={(value) =>
                  updateStartDate(value, transactionsEndDate, setTransactionsStartDate, setTransactionsEndDate)
                }
                sx={{ marginRight: 3 }}
                slotProps={{
                  textField: {
                    label: t('Transactions Start Date'),
                    fullWidth: true,
                    required: true,
                    variant: 'outlined',
                    error: submitted && !transactionsStartDate,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md>
              <DatePicker
                disableFuture
                value={transactionsEndDate}
                minDate={transactionsStartDate}
                maxDate={
                  transactionsStartDate
                    ? transactionsStartDate.add(MAX_RECONCILIATION_PERIOD_MONTHS, 'month')
                    : undefined
                }
                onChange={(value) => setTransactionsEndDate(value)}
                sx={{ marginRight: 3 }}
                slotProps={{
                  textField: {
                    label: t('Transactions End Date'),
                    fullWidth: true,
                    required: true,
                    variant: 'outlined',
                    error: submitted && !transactionsEndDate,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md>
              <Button disabled={isLoading} fullWidth type="submit" size="large" variant="contained">
                {t('Filter')}
                {isLoading && <CircularProgress size={24} sx={{ ml: 1, color: 'white' }} />}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {lastQuery && !isLoading && !error && rows.length === 0 && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {t('No reconciliation findings found for the selected filters')}
        </Alert>
      )}

      {rows.length > 0 && <PaymentsReconciliationTable rows={rows} />}

      <UploadStatementDialog open={openUpload} onClose={toggleUpload} />
    </div>
  );
}
